import React from "react";
import { formatCount } from "components/productSellerCompanyCard/productSellerCompanyCard.utils";
import { useGetCompanyXpQuery, type CompanyXp } from "reducers/expApi";
import { ProfileCard } from "./ProfileCard";

interface StructureScoreProps {
  companyCode?: string;
}

// Ngưỡng XP của level L là (L - 1)^2 * 100 (khớp backend LevelService).
const LEVEL_XP_BASE = 100;

// % tiến độ trong level hiện tại (0–100), dùng cho bề rộng thanh gradient.
const getLevelProgressPct = ({
  total_xp,
  current_level,
  xp_to_next_level,
}: CompanyXp): number => {
  const currentLevelFloor = (current_level - 1) ** 2 * LEVEL_XP_BASE;
  const nextThreshold = total_xp + xp_to_next_level;
  const band = nextThreshold - currentLevelFloor;
  if (band <= 0) {
    return 100;
  }
  const pct = ((total_xp - currentLevelFloor) / band) * 100;
  return Math.min(100, Math.max(0, pct));
};

const StructureScore = ({ companyCode }: StructureScoreProps) => {
  const { data, isLoading, isError } = useGetCompanyXpQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );

  const xp = data?.data;
  // Đang tải hoặc chưa có dữ liệu XP → trạng thái "スコア算出中…".
  const isEmpty = isLoading || isError || !xp;
  const progressPct = xp ? getLevelProgressPct(xp) : 0;

  return (
    <ProfileCard className="flex min-h-[472px] flex-col justify-center">
      <div className="border-y border-[#d4f754] py-[8px] text-center font-['Audiowide'] text-[22px] leading-none text-[#d4f754] md:text-[26px]">
        構造スコア
      </div>

      {isEmpty ? (
        <div className="mt-[50px] flex items-center justify-center gap-[16px]">
          <span className="h-[10px] w-[40px] rounded-[5px] bg-[#d4f754]/40" />
          <span className="h-[10px] w-[40px] rounded-[5px] bg-[#d4f754]/40" />
        </div>
      ) : (
        <div className="mt-[50px] text-center font-['Audiowide'] text-[76px] leading-none text-[#d4f754] md:text-[90px]">
          {formatCount(xp.total_xp)}
        </div>
      )}

      <div
        className={`mt-[28px] h-[20px] overflow-hidden rounded-[10px] bg-[#002959] ${
          isEmpty ? "opacity-40" : ""
        }`}
      >
        {!isEmpty && (
          <div
            className="h-full bg-[linear-gradient(90deg,#d4f754_0%,#2cdcec_75%,#ff4a84_100%)]"
            style={{ width: `${progressPct}%` }}
          />
        )}
      </div>

      {isEmpty && (
        <div className="mt-[14px] text-center font-['Noto_Sans_JP'] text-[13px] leading-[1.6] text-white/60">
          スコア算出中…
        </div>
      )}
    </ProfileCard>
  );
};

export default StructureScore;
